import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DataChecksContextProps {
  hasUploadedMemberData: boolean | null;
  hasReUploadedMemberData: boolean | null;
  hasUploadedProformaData: boolean | null;
  hasSelectedAssumptions: boolean | null;
  areAssumptionsFalseOnAssumptionsPage: boolean | null;
  hasUploadedDocuments: boolean | null;
  hasSectionsAndCategories: boolean | null;
  tabDashboardValue: number | null;
  lastDataUpload: string | null;
  setHasUploadedMemberData: (index: boolean | null) => void;
  setHasReUploadedMemberData: (index: boolean | null) => void;
  updateMemberDataStatusFunction: (index: boolean | null) => void;
  updateReMemberDataStatusFunction: (index: boolean | null) => void;
  updateProformaDataStatusFunction: (index: boolean | null) => void;
  updateAssumptionsStatusFunction: (index: boolean | null) => void;
  updateAreAssumptionsFalseOnAssumptionsPage: (index: boolean | null) => void;
  updateUploadedDocuments: (index: boolean | null) => void;
  updateSectionsAndCategories: (index: boolean | null) => void;
  updateTabDashboardValue: (index: number | null) => void;
  updateLastDataUpload: (index: string | null) => void;
}

const DataChecksContext = createContext<DataChecksContextProps | undefined>(undefined);

export const useDataChecks = (): DataChecksContextProps => {
  const context = useContext(DataChecksContext);
  if (!context) {
    throw new Error("useDataChecks must be used within a DataChecksContextProvider");
  }
  return context;
};

interface DataChecksProviderProps {
  children: ReactNode;
}

const STORAGE_VERSION = "v1.0.0";

const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return fallback;
    return JSON.parse(item);
  } catch {
    return fallback;
  }
};

const DataChecksContextProvider: React.FC<DataChecksProviderProps> = ({ children }) => {
  useEffect(() => {
    const storedVersion = localStorage.getItem("dataChecksVersion");
    if (storedVersion !== STORAGE_VERSION) {
      localStorage.clear();
      localStorage.setItem("dataChecksVersion", STORAGE_VERSION);
    }
  }, []);

  const [hasUploadedMemberData, setHasUploadedMemberData] = useState<boolean | null>(
    () => safeParse("hasUploadedMemberData", null)
  );
  const [hasReUploadedMemberData, setHasReUploadedMemberData] = useState<boolean | null>(
    () => safeParse("hasReUploadedMemberData", null)
  );
  const [hasUploadedProformaData, setHasUploadedProformaData] = useState<boolean | null>(
    () => safeParse("hasUploadedProformaData", null)
  );
  const [hasSelectedAssumptions, setHasSelectedAssumptions] = useState<boolean | null>(
    () => safeParse("hasSelectedAssumptions", false) 
  );
  const [areAssumptionsFalseOnAssumptionsPage, setAreAssumptionsFalseOnAssumptionsPage] = useState<boolean | null>(
    () => safeParse("areAssumptionsFalseOnAssumptionsPage", false) 
  );
  const [hasUploadedDocuments, setHasUploadedDocuments] = useState<boolean | null>(
    () => safeParse("hasUploadedDocuments", null)
  );
  const [hasSectionsAndCategories, setHasSectionsAndCategories] = useState<boolean | null>(
    () => safeParse("hasSectionsAndCategories", null)
  );
  const [tabDashboardValue, setTabDashboardValue] = useState<number | null>(
    () => safeParse("tabDashboardValue", null)
  );
  const [lastDataUpload, setLastDataUpload] = useState<string | null>(
    () => safeParse("lastDataUpload", null)
  );

  const syncToStorage = (key: string, value: any) => {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (err) {
      console.error(`Failed to sync ${key} to localStorage`, err);
    }
  };

  useEffect(() => syncToStorage("hasUploadedMemberData", hasUploadedMemberData), [hasUploadedMemberData]);
  useEffect(() => syncToStorage("hasReUploadedMemberData", hasReUploadedMemberData), [hasReUploadedMemberData]);
  useEffect(() => syncToStorage("hasUploadedProformaData", hasUploadedProformaData), [hasUploadedProformaData]);
  useEffect(() => syncToStorage("hasSelectedAssumptions", hasSelectedAssumptions), [hasSelectedAssumptions]);
  useEffect(() => syncToStorage("areAssumptionsFalseOnAssumptionsPage", areAssumptionsFalseOnAssumptionsPage), [areAssumptionsFalseOnAssumptionsPage]);
  useEffect(() => syncToStorage("hasUploadedDocuments", hasUploadedDocuments), [hasUploadedDocuments]);
  useEffect(() => syncToStorage("hasSectionsAndCategories", hasSectionsAndCategories), [hasSectionsAndCategories]);
  useEffect(() => syncToStorage("tabDashboardValue", tabDashboardValue), [tabDashboardValue]);
  useEffect(() => syncToStorage("lastDataUpload", lastDataUpload), [lastDataUpload]);

  const updateMemberDataStatusFunction = (index: boolean | null) => setHasUploadedMemberData(index);
  const updateReMemberDataStatusFunction = (index: boolean | null) => setHasReUploadedMemberData(index);
  const updateProformaDataStatusFunction = (index: boolean | null) => setHasUploadedProformaData(index);
  const updateAssumptionsStatusFunction = (index: boolean | null) => setHasSelectedAssumptions(index);
  const updateAreAssumptionsFalseOnAssumptionsPage = (index: boolean | null) => setAreAssumptionsFalseOnAssumptionsPage(index);
  const updateUploadedDocuments = (index: boolean | null) => setHasUploadedDocuments(index);
  const updateSectionsAndCategories = (index: boolean | null) => setHasSectionsAndCategories(index);
  const updateTabDashboardValue = (index: number | null) => setTabDashboardValue(index);
  const updateLastDataUpload = (index: string | null) => setLastDataUpload(index);

  const contextValue: DataChecksContextProps = {
    hasUploadedMemberData,
    hasReUploadedMemberData,
    hasUploadedProformaData,
    hasSelectedAssumptions,
    areAssumptionsFalseOnAssumptionsPage,
    hasUploadedDocuments,
    hasSectionsAndCategories,
    tabDashboardValue,
    lastDataUpload,
    updateMemberDataStatusFunction,
    updateReMemberDataStatusFunction,
    updateProformaDataStatusFunction,
    updateAssumptionsStatusFunction,
    updateAreAssumptionsFalseOnAssumptionsPage,
    updateUploadedDocuments,
    updateSectionsAndCategories,
    setHasUploadedMemberData,
    setHasReUploadedMemberData,
    updateTabDashboardValue,
    updateLastDataUpload
  };

  return (
    <DataChecksContext.Provider value={contextValue}>
      {children}
    </DataChecksContext.Provider>
  );
};

export default DataChecksContextProvider;
